export const SCREEN_RECORDING_STATUSES = {
  Idle: 'idle',
  Requesting: 'requesting',
  /** Granted, and wiring up the encoder and upload. Never gates the session. */
  Starting: 'starting',
  Recording: 'recording',
  Paused: 'paused',
  Finalizing: 'finalizing',
  Denied: 'denied',
  Unsupported: 'unsupported',
  Failed: 'failed',
} as const;

export type ScreenRecordingStatus =
  (typeof SCREEN_RECORDING_STATUSES)[keyof typeof SCREEN_RECORDING_STATUSES];

export const RECORDING_PAUSE_REASONS = {
  ScreenShareMuted: 'screenShareMuted',
  Inactivity: 'inactivity',
  Respondent: 'respondent',
} as const;

export type RecordingPauseReason =
  (typeof RECORDING_PAUSE_REASONS)[keyof typeof RECORDING_PAUSE_REASONS];

export const RECORDING_FAILURE_REASONS = {
  PermissionDenied: 'permissionDenied',
  Unsupported: 'unsupported',
  CaptureEnded: 'captureEnded',
  UploadFailed: 'uploadFailed',
} as const;

export type RecordingFailureReason =
  (typeof RECORDING_FAILURE_REASONS)[keyof typeof RECORDING_FAILURE_REASONS];

export interface RecordingFailure {
  readonly reason: RecordingFailureReason;
  readonly message: string | null;
  readonly isRetryable: boolean;
}

export const RECORDING_EVENT_TYPES = {
  PrimaryQuestion: 'primaryQuestion',
  AiFollowUp: 'aiFollowUp',
  UserTypedResponse: 'userTypedResponse',
  UserVoiceResponse: 'userVoiceResponse',
} as const;

export type RecordingEventType =
  (typeof RECORDING_EVENT_TYPES)[keyof typeof RECORDING_EVENT_TYPES];

export interface RecordingEvent {
  readonly messageId: string;
  readonly type: RecordingEventType;
  readonly atSeconds?: number;
}

export interface ScreenRecordingStartInput {
  readonly insightId: string;
  /**
   * True when the session holds the microphone itself, so the recording borrows
   * that one capture instead of opening a second. Passed in rather than inferred:
   * at the moment capture is granted the session has not yet run the effect that
   * opens its microphone, so anything the port could observe here would race.
   */
  readonly sharesSessionMicrophone: boolean;
  /**
   * True where the responder's speech is captured on a phone and relayed in, so
   * this page records the screen alone. Distinct from `sharesSessionMicrophone`:
   * there is no local microphone to borrow or to open.
   */
  readonly skipMicrophone?: boolean;
}

export interface ScreenRecordingPort {
  readonly status: ScreenRecordingStatus;
  readonly uploadProgress: number;
  readonly pauseReasons: ReadonlySet<RecordingPauseReason>;
  readonly failure: RecordingFailure | null;
  readonly start: (input: ScreenRecordingStartInput) => Promise<void>;
  readonly stop: () => Promise<void>;
  readonly pause: (reason: RecordingPauseReason) => void;
  readonly resume: (reason: RecordingPauseReason) => void;
  readonly markEvent: (event: RecordingEvent) => void;
  readonly elapsedSeconds: () => number | null;
}
